const CART_RESERVATION_TIMER_KEY = "iniraCartReservationEndsAt";

window.resetCartReservationTimer = function resetCartReservationTimer() {
  try {
    window.localStorage.removeItem(CART_RESERVATION_TIMER_KEY);
  } catch (error) {
    console.warn("Unable to reset cart reservation timer", error);
  }
};

class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Stores element that opened the drawer (for focus restore after close)
    this.openerEl = null;
  }

  connectedCallback() {
    if (
      this.classList.contains("wt-cart--empty") ||
      this.classList.contains("is-empty")
    ) {
      window.resetCartReservationTimer();
    }

    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  rememberOpener(e) {
    // Prefer currentTarget (the element with listener), fallback to activeElement
    const current = e?.currentTarget;
    const active = document.activeElement;

    // If click landed on svg/span inside button, climb up to interactive parent
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;

    // Wait one frame so DOM/classes settle after closing
    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");

      // Restore focus to the element that opened the drawer
      this.restoreFocusToOpener();
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();

        // Save opener only when opening
        if (!this.isOpen) this.rememberOpener(e);

        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

if (!customElements.get("cart-reservation-timer")) {
  customElements.define(
    "cart-reservation-timer",
    class CartReservationTimer extends HTMLElement {
      constructor() {
        super();
        this.timeElement = this.querySelector("[data-cart-reservation-time]");
        this.interval = null;
        this.onDrawerOpen = this.start.bind(this);
        this.onDrawerClose = this.stop.bind(this);
        this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      }

      connectedCallback() {
        try {
          this.endsAt = this.getEndsAt();
        } catch (error) {
          this.endsAt = Date.now() + this.getDuration();
        }
        this.tick();

        document.addEventListener(
          PUB_SUB_EVENTS.cartDrawerOpen,
          this.onDrawerOpen,
        );
        document.addEventListener(
          PUB_SUB_EVENTS.cartDrawerClose,
          this.onDrawerClose,
        );
        document.addEventListener("visibilitychange", this.onVisibilityChange);

        if (this.isDrawerOpen() && !document.hidden) {
          this.start();
        }
      }

      disconnectedCallback() {
        this.stop();
        document.removeEventListener(
          PUB_SUB_EVENTS.cartDrawerOpen,
          this.onDrawerOpen,
        );
        document.removeEventListener(
          PUB_SUB_EVENTS.cartDrawerClose,
          this.onDrawerClose,
        );
        document.removeEventListener(
          "visibilitychange",
          this.onVisibilityChange,
        );
      }

      getDuration() {
        const minutes = Number(this.dataset.countdownMinutes);
        const safeMinutes =
          Number.isFinite(minutes) && minutes > 0 ? minutes : 15;

        return safeMinutes * 60 * 1000;
      }

      isDrawerOpen() {
        return this.closest("cart-drawer")?.hasAttribute("open");
      }

      getEndsAt() {
        const now = Date.now();
        let endsAt = Number(
          window.localStorage.getItem(CART_RESERVATION_TIMER_KEY),
        );

        if (!Number.isFinite(endsAt) || endsAt <= now) {
          endsAt = now + this.getDuration();
          window.localStorage.setItem(
            CART_RESERVATION_TIMER_KEY,
            String(endsAt),
          );
        }

        return endsAt;
      }

      start() {
        if (!this.timeElement) return;

        if (!this.endsAt || this.endsAt <= Date.now()) {
          try {
            this.endsAt = this.getEndsAt();
          } catch (error) {
            this.endsAt = Date.now() + this.getDuration();
          }
        }

        this.tick();
        this.stop();
        this.interval = window.setInterval(() => this.tick(), 1000);
      }

      stop() {
        if (!this.interval) return;

        window.clearInterval(this.interval);
        this.interval = null;
      }

      handleVisibilityChange() {
        if (document.hidden) {
          this.stop();
          return;
        }

        if (this.isDrawerOpen()) {
          this.start();
        }
      }

      tick() {
        const remaining = Math.max(0, this.endsAt - Date.now());
        const totalSeconds = Math.ceil(remaining / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        this.timeElement.textContent = `${minutes}:${String(seconds).padStart(2, "0")}`;

        if (remaining <= 0) {
          this.stop();
        }
      }
    },
  );
}
